export type NetworkInterceptorCapturedRequest = {
	uniqueId: string;
	id?: string;
	itemName?: string;
	itemCategory?: string;
	reqApiUrl: string;
	reqMethod: string;
	domain?: string;
	timestamp?: number;
	/**
	 * Interceptor-captured response info (when available).
	 *
	 * NOTE: different capture sources store response content under different shapes:
	 * - `responseData.responseBody` (extension capture)
	 * - `response.responseBody` / `response.body` / `response.data` / `response.payload`
	 *
	 * The analyzer is defensive and supports multiple shapes.
	 */
	response?: any;
	responseData?: {
		resHeaders?: Array<{ key: string; value: string }>;
		responseBody?: any;
	};
	status?: number;
	statusText?: string;
	requestData?: {
		reqApiName?: string;
		reqApiUrl?: string;
		reqMethod?: string;
		reqBody?: {
			reqData?: string | null;
			reqType?: string;
			reqFormat?: string;
		};
		reqHeaders?: Array<{ key: string; value: string; enabled?: boolean }>;
		reqParams?: Array<{ key: string; value: string; enabled?: boolean }>;
	};
};

export type ExtractedParam = {
	/** Variable name without braces. Example: todoId */
	name: string;
	/** Which request produces this variable */
	producerRequestUniqueId: string;
	/** How to extract it (best-effort guess). Example: $.id */
	extractionPath: string;
	enabled: boolean;
};

export type UsedParam = {
	name: string;
	/** Where it is used in this request */
	locations: Array<'url' | 'body' | 'headers'>;
};

export type ParameterizedHeader = {
	key: string;
	value: string;
	enabled?: boolean;
};

export type ParameterizedRequest = {
	uniqueId: string;
	originalUrl: string;
	parameterizedUrl: string;
	method: string;
	originalBody: string | null;
	parameterizedBody: string | null;
	originalHeaders: ParameterizedHeader[];
	parameterizedHeaders: ParameterizedHeader[];
	usedParams: UsedParam[];
	extractedParams: ExtractedParam[];
};

export type NetworkInterceptorParameterizationModel = {
	requests: ParameterizedRequest[];
	allExtractedParams: ExtractedParam[];
};

function safeParseUrl(url: string): URL | null {
	try {
		// NOTE: if url contains {{var}} it won't parse; that's fine for captured requests (raw URLs)
		return new URL(url);
	} catch {
		return null;
	}
}

function singularize(resource: string) {
	if (!resource) return resource;
	if (resource.endsWith('ies')) return `${resource.slice(0, -3)}y`;
	if (resource.endsWith('s') && resource.length > 1) return resource.slice(0, -1);
	return resource;
}

function guessVarNameFromPathSegments(segments: string[], idSegmentIndex: number): string {
	const prev = segments[idSegmentIndex - 1] || 'resource';
	const base = singularize(prev.replace(/[^a-zA-Z0-9]/g, '')) || 'resource';
	return `${base}Id`;
}

function replaceAllExactSegment(pathname: string, targetSegment: string, replacement: string) {
	// pathname starts with "/"
	const parts = pathname.split('/');
	const out = parts.map((seg) => (seg === targetSegment ? replacement : seg));
	return out.join('/');
}

/**
 * Check if a value appears as a complete URL component (path segment or query param value).
 */
function valueAppearsAsCompleteUrlComponent(url: string, rawValue: string): boolean {
	const urlObj = safeParseUrl(url);
	if (!urlObj) {
		// Fallback: simple check if URL contains the value (less accurate)
		return url.includes(rawValue);
	}

	// Check path segments
	const segments = urlObj.pathname.split('/').filter(Boolean);
	if (segments.includes(rawValue)) return true;

	// Check query parameter values
	const queryEntries = Array.from(urlObj.searchParams.entries());
	for (const [, paramValue] of queryEntries) {
		if (paramValue === rawValue) return true;
	}

	return false;
}

/**
 * Replace a value in URL with boundary awareness.
 * Only replaces complete path segments or complete query parameter values.
 */
function replaceInUrlBoundaryAware(url: string, rawValue: string, replacement: string): string {
	const urlObj = safeParseUrl(url);
	if (!urlObj) {
		// Can't parse - return unchanged
		return url;
	}

	// Replace in path segments (exact segment match only)
	const pathname = replaceAllExactSegment(urlObj.pathname, rawValue, replacement);

	// Replace in query parameters (exact value match only)
	const newParams = new URLSearchParams();
	// let queryChanged = false;
	const entries = Array.from(urlObj.searchParams.entries());
	for (const [key, paramValue] of entries) {
		if (paramValue === rawValue) {
			newParams.append(key, replacement);
			// queryChanged = true;
		} else {
			newParams.append(key, paramValue);
		}
	}

	// Reconstruct URL without percent-encoding placeholders
	const queryString = newParams.toString();
	// Decode the placeholder back (URLSearchParams encodes {{ and }})
	const decodedQuery = queryString
		.replace(/%7B%7B/g, '{{')
		.replace(/%7D%7D/g, '}}');

	const search = decodedQuery ? `?${decodedQuery}` : '';
	return `${urlObj.origin}${pathname}${search}${urlObj.hash}`;
}

/**
 * Escape special regex characters for safe use in RegExp constructor.
 */
function escapeRegex(str: string): string {
	return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Recursively replace exact string values in a JSON object.
 * Only replaces when the entire value matches (not substrings).
 */
function replaceExactValuesInObject(obj: any, rawValue: string, replacement: string): any {
	if (obj === null || obj === undefined) return obj;

	if (Array.isArray(obj)) {
		return obj.map((item) => replaceExactValuesInObject(item, rawValue, replacement));
	}

	if (typeof obj === 'object') {
		const result: Record<string, any> = {};
		for (const [key, value] of Object.entries(obj)) {
			result[key] = replaceExactValuesInObject(value, rawValue, replacement);
		}
		return result;
	}

	// Only replace if the entire value matches exactly
	if (typeof obj === 'string' && obj === rawValue) {
		return replacement;
	}

	return obj;
}

/**
 * Check if a value appears as a complete JSON string value in the body.
 * This prevents partial substring matches.
 */
function valueAppearsAsCompleteJsonValue(body: string, rawValue: string): boolean {
	if (!body || !rawValue) return false;

	// Try to parse as JSON first for accurate detection
	const parsed = safeJsonParse(body);
	if (parsed && typeof parsed === 'object') {
		return checkValueExistsInObject(parsed, rawValue);
	}

	// Fallback: regex-based detection for JSON string values
	// Match "rawValue" as a complete JSON string value
	const escaped = escapeRegex(rawValue);
	const jsonStringPattern = new RegExp(`"${escaped}"`, 'g');
	return jsonStringPattern.test(body);
}

/**
 * Recursively check if a value exists as an exact match anywhere in an object.
 */
function checkValueExistsInObject(obj: any, rawValue: string): boolean {
	if (obj === null || obj === undefined) return false;

	if (Array.isArray(obj)) {
		return obj.some((item) => checkValueExistsInObject(item, rawValue));
	}

	if (typeof obj === 'object') {
		return Object.values(obj).some((value) => checkValueExistsInObject(value, rawValue));
	}

	// Exact match only
	return typeof obj === 'string' && obj === rawValue;
}

/**
 * Replace values in JSON body with boundary awareness.
 * Only replaces complete JSON values, not substrings within larger values.
 */
function replaceInBodyBoundaryAware(
	body: string | null,
	rawValue: string,
	replacement: string
): string | null {
	if (!body || typeof body !== 'string') return body;

	// Try to parse as JSON for precise replacement
	const parsed = safeJsonParse(body);
	if (parsed && typeof parsed === 'object') {
		const replaced = replaceExactValuesInObject(parsed, rawValue, replacement);
		try {
			// Preserve original formatting if possible (2-space indent is common)
			return JSON.stringify(replaced, null, 2);
		} catch {
			return JSON.stringify(replaced);
		}
	}

	// Fallback for non-JSON bodies: use regex to match complete quoted values
	const escaped = escapeRegex(rawValue);
	// Match "rawValue" as a complete JSON string value and replace with "replacement"
	const jsonStringPattern = new RegExp(`"${escaped}"`, 'g');
	return body.replace(jsonStringPattern, `"${replacement}"`);
}


/**
 * Check if a value appears as a complete value in headers.
 * Supports: exact match, Bearer token pattern, comma-separated values.
 */
function valueAppearsAsCompleteHeaderValue(headers: ParameterizedHeader[], rawValue: string): boolean {
	if (!Array.isArray(headers) || headers.length === 0) return false;

	return headers.some((h) => {
		const value = typeof h?.value === 'string' ? h.value : '';
		if (!value) return false;

		// Exact match
		if (value === rawValue) return true;

		// Bearer token pattern: "Bearer <token>"
		if (value === `Bearer ${rawValue}`) return true;

		// Check if it's a complete segment in comma-separated values
		const segments = value.split(',').map((s) => s.trim());
		if (segments.includes(rawValue)) return true;

		return false;
	});
}

/**
 * Replace values in headers with boundary awareness.
 * Only replaces complete values, not substrings within larger values.
 */
function replaceInHeadersBoundaryAware(
	headers: ParameterizedHeader[],
	rawValue: string,
	replacement: string
): ParameterizedHeader[] {
	if (!Array.isArray(headers) || headers.length === 0) return headers;

	return headers.map((h) => {
		const value = typeof h?.value === 'string' ? h.value : '';
		if (!value) return h;

		// Exact match - replace entire value
		if (value === rawValue) {
			return { ...h, value: replacement };
		}

		// Bearer token pattern: "Bearer <token>" -> "Bearer {{placeholder}}"
		if (value === `Bearer ${rawValue}`) {
			return { ...h, value: `Bearer ${replacement}` };
		}

		// Comma-separated values: replace only complete segments
		if (value.includes(',') && value.includes(rawValue)) {
			const segments = value.split(',').map((s) => s.trim());
			const hasExactSegment = segments.includes(rawValue);
			if (hasExactSegment) {
				const newSegments = segments.map((s) => (s === rawValue ? replacement : s));
				return { ...h, value: newSegments.join(', ') };
			}
		}

		// No match - return unchanged
		return h;
	});
}


function safeJsonParse(value: any): any {
	if (value === null || value === undefined) return undefined;
	if (typeof value === 'object') return value;
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	if (!trimmed) return undefined;
	try {
		return JSON.parse(trimmed);
	} catch {
		return undefined;
	}
}

function getResponseBodyObjectFromRequest(req: NetworkInterceptorCapturedRequest): any {
	if (!req) return undefined;

	// Prefer the extension's `responseData.responseBody` when present.
	const candidate =
		req?.responseData?.responseBody ??
		(req as any)?.responseData?.payload ??
		(req as any)?.responseData?.body ??
		(req as any)?.responseData?.resBody ??
		req?.response?.responseBody ??
		req?.response?.body ??
		req?.response?.data ??
		req?.response?.payload ??
		req?.response ??
		undefined;

	return safeJsonParse(candidate) ?? (typeof candidate === 'object' ? candidate : undefined);
}

function jsonPathForKey(parentPath: string, key: string) {
	if (/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key)) return `${parentPath}.${key}`;
	return `${parentPath}[${JSON.stringify(key)}]`;
}

type JsonLeaf = {
	value: string;
	path: string;
	key: string;
};

function collectJsonLeaves(obj: any, path = '$', parentKey = ''): JsonLeaf[] {
	const out: JsonLeaf[] = [];
	if (obj === null || obj === undefined) return out;

	if (Array.isArray(obj)) {
		obj.forEach((item, idx) => {
			out.push(...collectJsonLeaves(item, `${path}[${idx}]`, parentKey));
		});
		return out;
	}

	if (typeof obj === 'object') {
		for (const [k, v] of Object.entries(obj)) {
			out.push(...collectJsonLeaves(v, jsonPathForKey(path, k), k));
		}
		return out;
	}

	if (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean') {
		out.push({ value: String(obj), path, key: parentKey });
	}
	return out;
}

/**
 * Extract a meaningful variable name from the extraction path.
 * For paths like:
 *   - $.data.localData._id → "localDataId"
 *   - $.data.localData.items[0].activeDataSet._id → "activeDataSetId"
 *   - $.data.localData.items[0].team._id → "teamId"
 *   - $.user.accessToken → "accessToken"
 */
function guessVarNameFromExtractionPath(extractionPath: string): string | null {
	if (!extractionPath) return null;

	// Parse the path into segments
	// Handle both dot notation and bracket notation: $.data.items[0].field
	const normalized = extractionPath
		.replace(/^\$\.?/, '') // Remove leading $. or $
		.replace(/\[(\d+)\]/g, '.') // Remove array indices like [0]
		.replace(/\["([^"]+)"\]/g, '.$1') // Convert ["key"] to .key
		.replace(/\['([^']+)'\]/g, '.$1'); // Convert ['key'] to .key

	const segments = normalized.split('.').filter(Boolean);
	if (segments.length === 0) return null;

	const lastSegment = segments[segments.length - 1];
	const lastLower = lastSegment.toLowerCase();

	// If the last segment is id/_id, use the parent segment as context
	if (lastLower === 'id' || lastLower === '_id') {
		// Find the last meaningful parent (not a generic name like 'data', 'items', 'localData', 'result')
		const genericNames = new Set(['data', 'items', 'item', 'result', 'results', 'response', 'payload', 'body', 'content', 'value', 'values', 'list', 'array', 'object', 'localdata']);
		
		for (let i = segments.length - 2; i >= 0; i--) {
			const segment = segments[i];
			// Skip generic names and numeric indices
			if (!segment || /^\d+$/.test(segment) || genericNames.has(segment.toLowerCase())) {
				continue;
			}
			// Found a meaningful parent - use it as the variable name base
			const cleaned = segment.replace(/[^a-zA-Z0-9]/g, '');
			if (cleaned) {
				// Ensure camelCase: activeDataSet -> activeDataSetId
				return `${cleaned}Id`;
			}
		}
		// No meaningful parent found, just return "id"
		return 'id';
	}

	// For other endings like token, uuid, code, etc.
	return null; // Let the other logic handle it
}

function guessVarNameFromKeyAndUrl(key: string, requestUrl: string, extractionPath?: string) {
	// First, try to derive a meaningful name from the extraction path
	if (extractionPath) {
		const pathDerivedName = guessVarNameFromExtractionPath(extractionPath);
		if (pathDerivedName) {
			return pathDerivedName;
		}
	}

	const lower = (key || '').toLowerCase();
	// Prefer preserving the actual token key names when present.
	if (lower === 'accesstoken') return 'accessToken';
	if (lower === 'refreshtoken') return 'refreshToken';
	if (lower === 'rolecode') return 'roleCode';
	if (lower === 'id' || lower === '_id') {
		const url = safeParseUrl(requestUrl);
		if (url) {
			const segments = url.pathname.split('/').filter(Boolean);
			// Prefer the last non-numeric segment as the resource name (e.g. /todos -> todoId)
			const nonNumeric = segments.filter((s) => !/^\d+$/.test(s));
			const resource = nonNumeric[nonNumeric.length - 1] || 'resource';
			return `${singularize(resource)}Id`;
		}
		return 'id';
	}
	if (lower.includes('token')) return 'token';
	if (lower.includes('uuid')) return 'uuid';
	const cleaned = key.replace(/[^a-zA-Z0-9_]/g, '');
	return cleaned || 'var';
}

/**
 * Best-effort analyzer that:
 * - detects repeated numeric path segments (e.g. /todos/201)
 * - creates a variable name (e.g. todoId)
 * - chooses a "producer" request (prefer earlier POST /todos)
 * - parameterizes later URLs to use {{todoId}}
 */
export function analyzeNetworkInterceptorRequests(
	requests: NetworkInterceptorCapturedRequest[]
): NetworkInterceptorParameterizationModel {
	const toFiniteNumber = (v: any) => {
		const n = typeof v === 'number' ? v : Number(v);
		return Number.isFinite(n) ? n : undefined;
	};

	// Normalize to chronological order; be defensive because some capture sources emit `timestamp` as string.
	const normalized = (requests || [])
		.map((r, idx) => ({ r, idx, ts: toFiniteNumber((r as any)?.timestamp) }))
		.sort((a, b) => {
			// If both have timestamps, sort by ts; otherwise keep original relative order.
			if (a.ts !== undefined && b.ts !== undefined) return a.ts - b.ts;
			if (a.ts !== undefined) return -1;
			if (b.ts !== undefined) return 1;
			return a.idx - b.idx;
		})
		.map((x) => x.r);

	const producerIndexById = new Map<string, number>();
	normalized.forEach((req, idx) => producerIndexById.set(req.uniqueId, idx));

	const parsed = normalized.map((r) => ({
		r,
		url: safeParseUrl(r.reqApiUrl),
	}));

	// Prefer response-based extraction candidates when interceptor provides response data.
	type ResponseCandidate = {
		value: string;
		key: string;
		extractionPath: string;
		producerUniqueId: string;
		paramNameGuess: string;
	};

	const responseCandidates: ResponseCandidate[] = [];
	for (const r of normalized) {
		const responseObj = getResponseBodyObjectFromRequest(r);
		if (!responseObj) continue;
		const leaves = collectJsonLeaves(responseObj);
		for (const leaf of leaves) {
			const keyLower = (leaf.key || '').toLowerCase();
			// Extend beyond ids/tokens: allow "*Code" style values (e.g. roleCode -> COMPANY_ADMIN)
			const looksUseful = /(id|_id|uuid|token|code)$/.test(keyLower);
			if (!looksUseful) continue;
			// Filter obvious noise
			if (!leaf.value) continue;
			if (keyLower.includes('token') && leaf.value.length < 6) continue;
			// Avoid exploding candidate sets with huge blobs (but allow long JWTs)
			if (!keyLower.includes('token') && leaf.value.length > 500) continue;
			if (keyLower.includes('token') && leaf.value.length > 20000) continue;
			if (keyLower.includes('uuid') && leaf.value.length < 8) continue;
			// "code" is common (actionCode, screenCode, statusCode...). Keep only identifier-ish values.
			if (keyLower.endsWith('code')) {
				const v = leaf.value.trim();
				if (v.length < 2 || v.length > 120) continue;
				// allow: COMPANY_ADMIN, company-admin, companyAdmin, etc.
				if (!/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(v)) continue;
				// avoid turning HTTP status codes into vars (e.g. "200")
				if (/^\d{2,4}$/.test(v)) continue;
			}
			responseCandidates.push({
				value: leaf.value,
				key: leaf.key,
				extractionPath: leaf.path,
				producerUniqueId: r.uniqueId,
				paramNameGuess: guessVarNameFromKeyAndUrl(leaf.key || 'var', r.reqApiUrl, leaf.path),
			});
		}
	}

	// Collect numeric path segments by host + "template" (replace digits with :id) + actual value
	type Occurrence = {
		requestUniqueId: string;
		method: string;
		url: URL;
		idSegmentIndex: number;
		segments: string[];
		idValue: string;
	};

	const occurrences: Occurrence[] = [];
	for (const p of parsed) {
		if (!p.url) continue;
		const segments = p.url.pathname.split('/').filter(Boolean);
		segments.forEach((seg, idx) => {
			if (/^\d+$/.test(seg)) {
				occurrences.push({
					requestUniqueId: p.r.uniqueId,
					method: p.r.reqMethod,
					url: p.url!,
					idSegmentIndex: idx,
					segments,
					idValue: seg,
				});
			}
		});
	}

	// Group by host + path template + idValue
	const groupKey = (o: Occurrence) => {
		const templSegments = o.segments.map((s) => (/^\d+$/.test(s) ? ':id' : s));
		return `${o.url.host}|/${templSegments.join('/')}|${o.idValue}`;
	};

	const groups = new Map<string, Occurrence[]>();
	for (const o of occurrences) {
		const key = groupKey(o);
		const list = groups.get(key) || [];
		list.push(o);
		groups.set(key, list);
	}

	// Candidate variables: repeated groups (same id value used in multiple requests)
	const extractedParams: ExtractedParam[] = [];
	const idValueToParamName = new Map<string, string>();
	const producerByParamName = new Map<string, string>(); // paramName -> producerUniqueId

	for (const occs of Array.from(groups.values())) {
		if (occs.length < 2) continue;

		// Pick a stable variable name based on the first occurrence path.
		const first = occs[0];
		const guessedName = guessVarNameFromPathSegments(first.segments, first.idSegmentIndex);
		// De-dup names in case multiple groups produce same guess (todoId, todoId2, ...)
		let name = guessedName;
		let i = 2;
		while (extractedParams.some((p) => p.name === name)) {
			name = `${guessedName}${i++}`;
		}

		// Decide producer: prefer earliest POST to the collection endpoint (without id segment)
		const host = first.url.host;
		const resourcePath = `/${first.segments
			.filter((_, idx) => idx !== first.idSegmentIndex)
			.join('/')}`;

		const producerCandidate = parsed
			.filter((p) => p.url?.host === host)
			.filter((p) => p.r.reqMethod?.toUpperCase() === 'POST')
			.find((p) => p.url?.pathname === resourcePath);

		const producerUniqueId =
			producerCandidate?.r.uniqueId ?? occs[0].requestUniqueId /* fallback */;

		extractedParams.push({
			name,
			producerRequestUniqueId: producerUniqueId,
			extractionPath: '$.id',
			enabled: true,
		});

		idValueToParamName.set(first.idValue, name);
		producerByParamName.set(name, producerUniqueId);
	}

	// Track response-derived extracted params only when they are actually used downstream
	const responseExtractedParams: ExtractedParam[] = [];
	const responseValueToParamName = new Map<string, string>();

	const ensureUniqueParamName = (base: string) => {
		const cleaned = (base || 'var').trim() || 'var';
		let candidate = cleaned;
		let i = 2;
		while (
			responseExtractedParams.some((p) => p.name === candidate) ||
			extractedParams.some((p) => p.name === candidate)
		) {
			candidate = `${cleaned}${i++}`;
		}
		return candidate;
	};

	// Build per-request models
	const perRequest: ParameterizedRequest[] = normalized.map((r) => {
		const originalUrl = r.reqApiUrl;
		const originalBody =
			typeof r.requestData?.reqBody?.reqData === 'string'
				? r.requestData?.reqBody?.reqData
				: r.requestData?.reqBody?.reqData === null
					? null
					: null;

		const originalHeaders: ParameterizedHeader[] = Array.isArray(r.requestData?.reqHeaders)
			? (r.requestData?.reqHeaders as any[]).map((h) => ({
					key: String((h as any)?.key ?? ''),
					value: String((h as any)?.value ?? ''),
					enabled: (h as any)?.enabled,
			  }))
			: [];

		let parameterizedUrl = originalUrl;
		let parameterizedBody = originalBody;
		let parameterizedHeaders = originalHeaders;

		const used: UsedParam[] = [];

		// 1) Response-based substitution (preferred)
		const currentIdx = producerIndexById.get(r.uniqueId) ?? 0;
		const priorResponseCandidates = responseCandidates
			.filter((c) => (producerIndexById.get(c.producerUniqueId) ?? -1) < currentIdx)
			.sort((a, b) => b.value.length - a.value.length);

		for (const c of priorResponseCandidates) {
			// Use boundary-aware detection: only match complete values, not substrings
			const appearsInUrl = valueAppearsAsCompleteUrlComponent(parameterizedUrl, c.value);
			const appearsInBody =
				!!parameterizedBody && valueAppearsAsCompleteJsonValue(parameterizedBody, c.value);
			const appearsInHeaders = valueAppearsAsCompleteHeaderValue(
				parameterizedHeaders,
				c.value
			);
			if (!appearsInUrl && !appearsInBody && !appearsInHeaders) continue;

			let name = responseValueToParamName.get(c.value);
			if (!name) {
				// If heuristic already mapped a numeric id value, reuse that var name so everything stays consistent
				const existingHeuristicName = /^\d+$/.test(c.value)
					? idValueToParamName.get(c.value)
					: undefined;
				name = existingHeuristicName || ensureUniqueParamName(c.paramNameGuess);
				responseValueToParamName.set(c.value, name);
				// If it's numeric, align heuristic map too (helps URL segment replacement later)
				if (/^\d+$/.test(c.value) && !idValueToParamName.has(c.value)) {
					idValueToParamName.set(c.value, name);
				}
				// Only add extracted param if we don't already have it
				if (!extractedParams.some((p) => p.name === name)) {
					responseExtractedParams.push({
						name,
						producerRequestUniqueId: c.producerUniqueId,
						extractionPath: c.extractionPath || '$.id',
						enabled: true,
					});
				}
			}

			const placeholder = `{{${name}}}`;

			// Use boundary-aware replacement: only replace complete values, not substrings
			if (appearsInUrl) {
				parameterizedUrl = replaceInUrlBoundaryAware(parameterizedUrl, c.value, placeholder);
			}
			if (appearsInBody && parameterizedBody) {
				parameterizedBody = replaceInBodyBoundaryAware(
					parameterizedBody,
					c.value,
					placeholder
				);
			}
			if (appearsInHeaders) {
				parameterizedHeaders = replaceInHeadersBoundaryAware(
					parameterizedHeaders,
					c.value,
					placeholder
				);
			}

			const locations: UsedParam['locations'] = [];
			if (appearsInUrl) locations.push('url');
			if (appearsInBody) locations.push('body');
			if (appearsInHeaders) locations.push('headers');

			const existing = used.find((u) => u.name === name);
			if (existing) {
				existing.locations = Array.from(new Set([...existing.locations, ...locations]));
			} else {
				used.push({ name, locations });
			}
		}

		// 2) Heuristic fallback: numeric id segments in URLs
		const urlObj = safeParseUrl(originalUrl);
		if (urlObj) {
			const segments = urlObj.pathname.split('/').filter(Boolean);
			let currentPathname = urlObj.pathname;
			const numericSegments = segments.filter((s) => /^\d+$/.test(s));
			for (const idValue of numericSegments) {
				const name = idValueToParamName.get(idValue);
				if (!name) continue;
				const placeholder = `{{${name}}}`;

				// Replace exact segment occurrence(s) in URL path
				currentPathname = replaceAllExactSegment(currentPathname, idValue, placeholder);
				// IMPORTANT: avoid `urlObj.toString()` because it percent-encodes `{` and `}`
				parameterizedUrl = `${urlObj.origin}${currentPathname}${urlObj.search}${urlObj.hash}`;

				// Replace in body too if present (boundary-aware: only exact JSON values)
				const bodyHasExactMatch = valueAppearsAsCompleteJsonValue(
					parameterizedBody,
					idValue
				);
				if (bodyHasExactMatch && parameterizedBody) {
					const newBody = replaceInBodyBoundaryAware(
						parameterizedBody,
						idValue,
						placeholder
					);
					if (newBody !== parameterizedBody) {
						parameterizedBody = newBody;
					}
				}

				// Replace in headers too (boundary-aware: only complete header values)
				const headersHaveExactMatch = valueAppearsAsCompleteHeaderValue(
					parameterizedHeaders,
					idValue
				);
				let headersChanged = false;
				if (headersHaveExactMatch) {
					const newHeaders = replaceInHeadersBoundaryAware(
						parameterizedHeaders,
						idValue,
						placeholder
					);
					headersChanged = newHeaders !== parameterizedHeaders;
					parameterizedHeaders = newHeaders;
				}

				const locations: UsedParam['locations'] = ['url'];
				if (parameterizedBody !== originalBody && parameterizedBody?.includes(placeholder)) {
					locations.push('body');
				}
				if (headersChanged) {
					locations.push('headers');
				}
				const existing = used.find((u) => u.name === name);
				if (existing) {
					existing.locations = Array.from(new Set([...existing.locations, ...locations]));
				} else {
					used.push({ name, locations });
				}
			}
		}

		return {
			uniqueId: r.uniqueId,
			originalUrl,
			parameterizedUrl,
			method: r.reqMethod,
			originalBody,
			parameterizedBody,
			originalHeaders,
			parameterizedHeaders,
			usedParams: used,
			// NOTE: extracted params are attached in a second pass after we discover all response-derived vars.
			extractedParams: [],
		};
	});

	// Second pass: attach extracted params to the producer request that generates them.
	// This is required because response-derived params are often discovered when processing *downstream* requests.
	const allExtractedParams = [...responseExtractedParams, ...extractedParams];
	const requestsWithExtracted: ParameterizedRequest[] = perRequest.map((r) => ({
		...r,
		extractedParams: allExtractedParams.filter((p) => p.producerRequestUniqueId === r.uniqueId),
	}));

	return {
		requests: requestsWithExtracted,
		allExtractedParams,
	};
}


